import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as fromActions from './consent-actions';
import {exhaustMap, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {OutlineUnitBasicModel} from '@core/modules/rest/consent/response/outline-unit-basic.model';
import {CommentBasicModel} from '@core/modules/rest/consent/response/comment-basic.model';
import {OutlineChapterUnitWithMarkerBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-with-marker-basic.model';

@Injectable()
export class ConsentEffects {

    constructor(private actions$: Actions, private consentRestService: ConsentRestService) {
    }

    getConsent$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.GetConsent),
            exhaustMap((action: ReturnType<typeof fromActions.GetConsent>) => {
                return this.consentRestService.getConsent({
                    ...action.consent
                })
                    .pipe(
                        map((response: OutlineChapterUnitWithMarkerBasicModel) => fromActions.GetConsentSuccess({
                            consent: response.chapterUnits,
                            currentUnitOrderId: response.currentUnitOrderId
                        }))
                    );
            })
        )
    );

    getConsentDuration$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.GetConsentDuration),
            exhaustMap((action: ReturnType<typeof fromActions.GetConsentDuration>) => {
                return this.consentRestService.getConsentDuration(action.consent)
                    .pipe(
                        map((duration: number) => fromActions.GetConsentDurationSuccess({
                            duration
                        }))
                    );
            })
        )
    );

    getMoreUnits$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.LoadMoreUnits),
            exhaustMap((action: ReturnType<typeof fromActions.LoadMoreUnits>) => {
                return this.consentRestService.getMoreUnits(action.request)
                    .pipe(
                        map((response: OutlineUnitBasicModel[]) => fromActions.LoadMoreUnitsSuccess({
                            unitOrderId: action.request.unitOrderId,
                            units: response,
                            viewDuration: action.request.viewDuration
                        }))
                    );
            })
        )
    );

    submitConsent$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.SubmitConsent),
            exhaustMap((action: ReturnType<typeof fromActions.SubmitConsent>) => {
                return this.consentRestService.submitConsent(action.request)
                    .pipe(
                        map(() =>
                            fromActions.SubmitConsentSuccess()
                        )
                    );
            })
        )
    );

    submitComment$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.SubmitComment),
            exhaustMap((action: ReturnType<typeof fromActions.SubmitComment>) => {
                return this.consentRestService.submitComment(action.request)
                    .pipe(
                        map((comment: CommentBasicModel) => fromActions.SubmitCommentSuccess({
                            comment
                        }))
                    );
            })
        )
    );

    getConsentComments$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.GetConsentComments),
            exhaustMap((action: ReturnType<typeof fromActions.GetConsentComments>) => {
                return this.consentRestService.getConsentComments(action.consentId)
                    .pipe(
                        map((response: CommentBasicModel[]) => fromActions.GetConsentCommentsSuccess({
                            comments: response
                        }))
                    );
            })
        )
    );

    deleteComment$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.DeleteComment),
            exhaustMap((action: ReturnType<typeof fromActions.DeleteComment>) => {
                return this.consentRestService.deleteComment(action.commentId)
                    .pipe(
                        map(() => {
                            return fromActions.GetConsentComments({consentId: action.consentId});
                        })
                    );
            })
        )
    );

    editComment$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.EditComment),
            exhaustMap((action: ReturnType<typeof fromActions.EditComment>) => {
                return this.consentRestService.editComment(action.request)
                    .pipe(
                        map((comment: CommentBasicModel) => fromActions.EditCommentSuccess({
                            response: comment
                        }))
                    );
            })
        )
    );

    answerComment$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.AnswerComment),
            exhaustMap((action: ReturnType<typeof fromActions.AnswerComment>) => {
                return this.consentRestService.answerComment(action.request)
                    .pipe(
                        map((comment: CommentBasicModel) => fromActions.EditCommentSuccess({
                            response: comment
                        }))
                    );
            })
        )
    );


    savePatientOverview$ = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.SavePatientOverviewDuration),
            exhaustMap((action: ReturnType<typeof fromActions.SavePatientOverviewDuration>) => {
                return this.consentRestService.saveOverviewDuration(action.consentId, action.request)
                    .pipe(
                        map(() => fromActions.FinishEducation({
                                consentId: action.consentId
                            }
                        ))
                    );
            })
        )
    );

    finishPatientEducation = createEffect(() => this.actions$
        .pipe(
            ofType(fromActions.FinishEducation),
            exhaustMap((action: ReturnType<typeof fromActions.FinishEducation>) => {
                return this.consentRestService.finishPatientEducation(action.consentId)
                    .pipe(
                        map(() => fromActions.FinishEducationSuccess())
                    );
            })
        )
    );
}
